import { Field, Form, Formik } from 'formik'
import { type FC } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/shared/ui/Button/Button'
import {
  TextField,
  CheckBoxField,
  DateField,
  MultiSelectField,
  TextAreaField,
} from '@/shared/ui/FormFields'
import { useCreatePromotionForm } from '../lib/use-create-promotion-form'
import { createPromotionFormSchema } from '../lib/consts'
import { Loader } from '@mantine/core'
import ImageCropProvider from '@/app/providers/ImageCropProvider'
import PhotoUploder from '@/features/Photos/UploadAndFrontCrop/PhotoUploader'
import { useMultiSelectWithSearch } from '@/shared/lib/useMultiSelectWithSearch'

type CreatePromotionFormProps = {
  onCloseModal: () => void
}

export const CreatePromotionForm: FC<CreatePromotionFormProps> = props => {
  const { onCloseModal } = props
  const { t } = useTranslation(['promotion'])
  const {
    initialValues,
    isLoadingCreatePromotion,
    submitForm,
    setSearchQuery,
    residentialProjects,
    isLoading,
  } = useCreatePromotionForm({ onCloseModal })

  const { createOptionsWithPreservedSelections, saveSelectedOptions } = useMultiSelectWithSearch()

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={submitForm}
      enableReinitialize
      validationSchema={createPromotionFormSchema(t)}
    >
      {({ values, setFieldValue, setFieldTouched }) => {
        const residentialOptions = createOptionsWithPreservedSelections(
          residentialProjects,
          values.residential_projects || [],
        )

        return (
          <Form>
            <div className='flex flex-row items-center space-x-4 mb-4 pt-4'>
              <CheckBoxField
                name='is_permanent'
                label={t('promotion:is_permanent')}
                className='flex flex-col w-full mb-4'
                disabled={isLoadingCreatePromotion}
                onChangeChecked={checked => {
                  if (checked) {
                    setFieldValue('end_date', null)
                    setFieldTouched('end_date', false, false)
                  }
                }}
              />
            </div>
            <TextField
              name='name'
              label={t('promotion:name')}
              className='flex flex-col w-full mb-4'
              required
              disabled={isLoadingCreatePromotion}
            />
            <div className='flex flex-row items-center space-x-4'>
              <DateField
                name='start_date'
                label={t('promotion:start_date')}
                className='flex flex-col w-full mb-4'
                required
              />
              <DateField
                name='end_date'
                label={t('promotion:end_date')}
                className='flex flex-col w-full mb-4'
                required={!values.is_permanent}
                disabled={values.is_permanent}
              />
            </div>
            <MultiSelectField
              name='residential_projects'
              label={t('promotion:projects')}
              className='flex flex-col w-full mb-4'
              options={residentialOptions}
              isSearchable={true}
              required
              disabled={isLoadingCreatePromotion}
              nothingFound={
                isLoading ? (
                  <div className='flex w-full items-center flex-col'>
                    <div className='flex w-[1.25rem] h-[1.25rem] justify-center items-center'>
                      <Loader size='xs' color='blue' />
                    </div>
                  </div>
                ) : (
                  t('common:nothing_found')
                )
              }
              onSearchChange={setSearchQuery}
              onSelectionChange={selectedIds => {
                saveSelectedOptions(residentialProjects, selectedIds)
              }}
            />
            <Field name='thumbnail'>
              {({ form: { values, setFieldValue } }) => (
                <ImageCropProvider>
                  <PhotoUploder
                    label={t('promotion:thumbnail')}
                    initialValue={values.thumbnail}
                    name={'thumbnail'}
                    setField={setFieldValue}
                    aspectRetio={4 / 3}
                  />
                </ImageCropProvider>
              )}
            </Field>
            <TextAreaField
              name='description'
              label={t('promotion:description')}
              className='flex flex-col w-full mb-4'
              placeholder='#Markdown'
              required
            />
            <div className='flex flex-row w-full'>
              <Button type='submit' size='md' color='base' disabled={isLoadingCreatePromotion}>
                {isLoadingCreatePromotion ? (
                  <Loader size='xs' color='white' />
                ) : (
                  t('promotion:buttons.add_promotion')
                )}
              </Button>
            </div>
          </Form>
        )
      }}
    </Formik>
  )
}
